/**
 * THE ONE FILE THIS APP WRITES ITSELF. Everything else in this folder was copied from
 * `supplier-order-app` at `d12fe53` on 2026-10-07 (the pilot on /consolidated-business); the
 * adaptations are listed in `context/docs/whats-new.md`.
 *
 * `satisfies WhatsNewEntry[]` breaks the build on a misspelled `kind` or a missing `title`.
 *
 * ## What earns an entry
 *
 * **Only a change a user can notice.** A refactor, a migration, a corrected grant: no entry.
 * Only Roman confirms an entry's text. The style guide is `README.md` beside this file: one
 * sentence about what the reader now gets, in English.
 *
 * `id` is stable, dated and never reused: it is the key of a row in `[Reporting].[WhatsNewSeen]`.
 * `major: true` shows a blocking dialog once — the bar is "they would be worse off not knowing
 * before they start", a couple of times a year.
 *
 * ## `page` — this app's addition
 *
 * This app is twenty-odd pages with different audiences. An entry with `page` is shown only on
 * that route (and its sub-routes), and only somebody who can open the page ever gets there; an
 * entry without `page` is about the whole app and shows everywhere. Pick `page` unless the change
 * really concerns everybody.
 */

import type { WhatsNewEntry } from './types';

export const entries = [
    {
        id: '2026-10-07-skpi-design-update',
        /* Text approved by Roman on 2026-10-07 (strategic KPIs round 2, K5), as drafted ("Kurzfassung").
           Not `major`: the figures are the same, only how they are shown changed. No anchor — the
           change runs through the whole dashboard and the evidence modal. */
        date: '2026-10-07',
        kind: 'improved',
        major: false,
        page: '/strategic-kpis',
        title: 'Calmer status colours, readable evidence',
        body: 'Status badges are now grey with a coloured dot, and the prior-year arrow says better or worse. '
            + 'Market data shows its share of the year target until the year end. The rows behind a figure '
            + 'have readable column names.',
    },
    {
        id: '2026-10-07-skpi-new-kpis-2027',
        /* Text approved by Roman on 2026-10-07 (strategic KPIs round 2, K3c), as drafted ("Kurz").
           Not `major`: four KPIs added, nothing taken away. No anchor — they sit in the matrix,
           Definitions and the Targets tab, not in one control. */
        date: '2026-10-07',
        kind: 'new',
        major: false,
        page: '/strategic-kpis',
        title: 'New KPIs for the 2027 targets',
        body: 'GP Cosmetics, GP Adhesive, GP without Kraton and the number of adhesive suppliers are now '
            + 'in the matrix and Definitions, and take targets at every level in the Targets tab.',
    },
    {
        id: '2026-10-07-skpi-definitions-v4',
        /* Text approved by Roman on 2026-10-07 (strategic KPIs round 2, K3b), as drafted ("Kurz").
           Not `major`: two figures are recomputed and lose their target, nothing a user must know
           before starting. No anchor — the change is in the figures, not in one control. */
        date: '2026-10-07',
        kind: 'improved',
        major: false,
        page: '/strategic-kpis',
        title: 'Success rate and days to close redefined',
        body: 'Success Rate and Avg Days now count only projects that reached Industrial trial or '
            + 'Negotiation, by the real won/lost date, and are shown as context without a target; '
            + 'Green Deals are gone.',
    },
    {
        id: '2026-10-07-skpi-selection-filter',
        /* Text approved by Roman on 2026-10-07 (strategic KPIs round 2, K3), as drafted ("Kurz").
           Not `major`: an added filter, nothing taken away. */
        date: '2026-10-07',
        kind: 'new',
        major: false,
        page: '/strategic-kpis',
        title: 'Filter by company, team and person',
        body: 'Next to the BU you can now pick several companies, teams and persons; the dashboard and '
            + 'the Targets tab show just that selection, and the link keeps it.',
        anchor: 'filter:skpi-selection',
    },
    {
        id: '2026-10-07-skpi-total-visits-target',
        /* Text approved by Roman on 2026-10-07 (strategic KPIs round 2, K2), as drafted. Not
           `major`: nothing a user relies on is taken away — C is still shown, just calculated. */
        date: '2026-10-07',
        kind: 'improved',
        major: false,
        page: '/strategic-kpis',
        title: 'Total visits target',
        body: 'In Targets you now set one total visits target per person or group; the C-visit target '
            + 'is calculated as Total − A − B and shown read-only next to it.',
        anchor: 'column:skpi-total-visits-target',
    },
    {
        id: '2026-10-07-cb-forward-to-colleague',
        /* Text approved by Roman on 2026-10-07, as drafted. Not `major`: nothing is taken away
           that a user relies on before they start. */
        date: '2026-10-07',
        kind: 'improved',
        major: false,
        page: '/consolidated-business',
        title: 'Forward to a colleague',
        body: 'Consolidated Business no longer e-mails customers. Use "Forward to colleague" in a '
            + "combination's panel to send the customer and the products you tick to colleagues; "
            + 'only Dolder employees can be chosen. "Sent e-mails" now shows whether a mail went to '
            + 'a colleague or, before today, to the customer.',
        anchor: 'button:cb-forward',
    },
    {
        id: '2026-10-19-cars-documents',
        /* Text approved by Roman on 2026-10-19, as drafted. Not `major`: an addition, nothing in
           the fleet list changed meaning. No anchor — the Docs column repeats on every row. */
        date: '2026-10-19',
        kind: 'new',
        major: false,
        page: '/company-cars',
        title: 'Documents on every car',
        body: 'Upload the registration certificate, contracts and insurance policy to a car and open them '
            + 'right here; the Docs column shows how many there are and when the next one expires.',
    },
    {
        id: '2026-10-20-training-log-peopledoc-hr',
        /* Body approved by Roman on 2026-10-20 (/ship), as proposed. Not `major`: managers keep the
           same tab, only its members follow PeopleDoc; the HR tab is an addition. No anchor — the
           change concerns two tabs. */
        date: '2026-10-20',
        kind: 'improved',
        major: false,
        page: '/training-log',
        title: 'Teams from PeopleDoc, overview for HR',
        body: 'Managers now see their team as recorded in PeopleDoc, and HR gets an All Employees tab '
            + 'with totals and CSV export.',
    },
    {
        id: '2026-10-20-skpi-redesign',
        /* Text approved by Roman on 2026-10-20 (R1, strategic KPIs redesign, E17). Not `major`: the figures
           are the same, only where and how they are shown changed. No anchor — the change runs
           through the whole page. No entry per definition version (E14). */
        date: '2026-10-20',
        kind: 'improved',
        major: false,
        page: '/strategic-kpis',
        title: 'Strategic KPIs, reorganised',
        body: 'The page now has five tabs: Dashboard, People, Targets, Data quality and Definitions. '
            + 'Details open in a side panel instead of pop-ups and stacked tables. '
            + "In Targets you copy last year's targets with a preview first, then correct them one KPI family at a time.",
    },
    {
        id: '2026-10-21-training-log-month-end-reminder',
        /* Text approved by Roman on 2026-10-20 (training reminder plan, C3), as drafted. Shipped with
           the go-live push on 2026-10-21 (Roman), after the reminder was switched on; the first run is
           Fri 2026-10-30 (E20). Not `major`: the mail itself tells everybody what to do. No anchor —
           the change is a mail, a banner and an HR table. */
        date: '2026-10-21',
        kind: 'new',
        major: false,
        page: '/training-log',
        title: 'Month-end reminder from HR',
        body: 'If you have not logged any training by the last working day of a month, HR sends you a short email. '
            + 'One click in the app confirms there was none, or you add what is missing. '
            + 'HR sees each month who logged a training, who confirmed and who has not replied yet.',
    },
    {
        id: '2026-10-23-credit-limits-overview',
        /* Text approved by Roman on 2026-10-23 (credit-limits v3, R6 at Gate A), as drafted in A6. Shown only on
           /credit-limits (Layout filters by `page`), which until R3 (Gate B) only Finance can open. */
        date: '2026-10-23',
        kind: 'new',
        major: false,
        page: '/credit-limits',
        title: 'Credit limits as a table of all customers',
        body: 'The page now lists every Dolder-Bigler customer with limit, balance, preview including open orders '
            + 'and overdue marking. Filter, sort and resize the columns, and right-click a row for open invoices, '
            + 'orders, sales, comments or an e-mail to the salesperson.',
    },
    {
        id: '2026-10-24-credit-limits-requests',
        /* Text approved by Roman at R6 (Gate B, E38), as drafted in B6. Shipped on 2026-10-28 with R3, which gave the
           sellers, the CEO and Inside Sales / Logistics the page. Not `major`: the page is new to most of its readers
           anyway. No anchor: the change runs through three tabs. Shown only on /credit-limits. */
        date: '2026-10-28',
        kind: 'new',
        major: false,
        page: '/credit-limits',
        title: 'Change a credit limit in the table',
        body: 'Salespeople and BT managers change a limit right in its cell, and the request goes to the people its '
            + 'amount requires. The Requests tab shows who decides next, and Finance keeps the amounts and people '
            + 'under Approval rules.',
    },
    {
        id: '2026-10-27-credit-limits-nav',
        /* Text approved by Roman on 2026-10-28 (R6 at Gate C), as drafted in C3. Shipped with R3 after the first live
           write (R5, #19) was confirmed by the nightly run. Not `major`. Shown only on /credit-limits. */
        date: '2026-10-28',
        kind: 'new',
        major: false,
        page: '/credit-limits',
        title: 'Approved limits go into NAV from the app',
        body: 'Once a request is approved, Finance writes the new limit into NAV with one click, and the app reads it '
            + 'back. The day after, the request shows that the nightly run has confirmed the value.',
    },
] satisfies WhatsNewEntry[];
